import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@strivelabs/ui-core"
import { Button } from "@strivelabs/ui-core/v2"
import { Loader2 } from "lucide-react"
import { PasswordInput } from "@/components/common"
import { useCreateOAuthClientConnection } from "@/hooks/useConnections"
import { logger } from "@/lib/logger"
import type { App } from "@/services/DataService"

interface OAuthClientConnectModalProps {
  app: App | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Called once the connection is saved and the authorization window is open.
   * Carries both so the caller can open post-connection config after consent.
   */
  onSuccess?: (result: { credentialId: string; popup: Window | null }) => void
}

const formSchema = z.object({
  display_name: z
    .string()
    .trim()
    .min(1, "Connection name is required"),
  client_id: z
    .string()
    .trim()
    .min(1, "Client ID is required"),
  client_secret: z
    .string()
    .trim()
    .min(1, "Client secret is required"),
})

type FormData = z.infer<typeof formSchema>

/**
 * Connects an app whose OAuth client belongs to the customer rather than to us.
 *
 * A Salesforce Connected App is created by the customer's own admin, so its
 * Consumer Key/Secret differ per tenant and there is no platform-wide client to
 * fall back on. The pair is collected here, saved on the connection, and then
 * used to authorize — the popup opens straight after saving, so this reads as a
 * single "Connect" to the user.
 */
export function OAuthClientConnectModal({
  app,
  open,
  onOpenChange,
  onSuccess,
}: OAuthClientConnectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const connect = useCreateOAuthClientConnection()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { display_name: "", client_id: "", client_secret: "" },
  })

  // Blank on every open. The secret is write-only — the API never returns one,
  // so there is nothing to prefill even when re-connecting.
  useEffect(() => {
    if (open && app) {
      reset({ display_name: app.display_name, client_id: "", client_secret: "" })
      setSubmitError(null)
    }
  }, [open, app, reset])

  const onSubmit = async (form: FormData) => {
    if (!app) return

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const { credential, popup } = await connect.mutateAsync({
        app_name: app.short_name,
        auth_name: app.auth[0]?.short_name ?? "default",
        display_name: form.display_name,
        token_type: "oauth",
        client_id: form.client_id,
        client_secret: form.client_secret,
      })

      // The connection is saved either way; only the authorization is missing.
      // It can be finished later from the connection's "Authorize" action.
      if (!popup) {
        toast({
          title: "Allow pop-ups to finish",
          description: `${app.display_name} was saved, but the authorization window was blocked.`,
          variant: "destructive",
        })
      } else {
        toast({
          title: "Almost there",
          description: `Finish authorizing ${app.display_name} in the window that just opened.`,
        })
      }

      onOpenChange(false)
      onSuccess?.({ credentialId: credential.id, popup })
    } catch (error) {
      // Log only the message so the client_secret can't leak via an error
      // object that echoes the request body.
      const message = error instanceof Error ? error.message : "Unknown error"
      logger(new Error(message), { context: "OAuthClientConnectModal: connect failed" })
      setSubmitError("We couldn't save these credentials. Please check the values and try again.")
      toast({
        title: "Connection Failed",
        description: "We couldn't save these credentials. Please check the values and try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!isSubmitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect {app?.display_name ?? ""}</DialogTitle>
          <DialogDescription>
            Paste the Consumer Key and Secret from your {app?.display_name ?? "provider"} Connected
            App. They are stored encrypted and used only to connect on your behalf.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="oauth-client-display-name">
              Connection name <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="oauth-client-display-name"
              placeholder={`e.g., My ${app?.display_name ?? ""} org`}
              disabled={isSubmitting}
              {...register("display_name")}
              className={errors.display_name ? "border-red-500" : ""}
            />
            {errors.display_name && (
              <p className="text-xs text-red-500">{errors.display_name.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="oauth-client-id">
              Client ID <sup className="text-red-500">*</sup>
            </Label>
            <Input
              id="oauth-client-id"
              placeholder="Consumer Key from your Connected App"
              autoComplete="off"
              spellCheck={false}
              disabled={isSubmitting}
              {...register("client_id")}
              className={errors.client_id ? "border-red-500" : ""}
            />
            {errors.client_id && (
              <p className="text-xs text-red-500">{errors.client_id.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="oauth-client-secret">
              Client secret <sup className="text-red-500">*</sup>
            </Label>
            <PasswordInput
              id="oauth-client-secret"
              placeholder="Consumer Secret from your Connected App"
              autoComplete="off"
              spellCheck={false}
              disabled={isSubmitting}
              {...register("client_secret")}
              className={errors.client_secret ? "border-red-500" : ""}
            />
            {errors.client_secret && (
              <p className="text-xs text-red-500">{errors.client_secret.message}</p>
            )}
          </div>

          {submitError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save and connect"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
